import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X } from 'lucide-react';

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 10000; background: rgba(2,6,23,.8);
  backdrop-filter: blur(6px); display: grid; place-items: center; padding: 24px;
  @media(max-width: 700px) { padding: 8px; }
`;
const Dialog = styled.div`
  width: min(1180px, 100%); height: min(860px, calc(100dvh - 48px));
  display: flex; flex-direction: column; overflow: hidden; border-radius: 16px;
  border: 1px solid #334155; background: #0b1220; color: #e2e8f0;
  box-shadow: 0 24px 80px #0008;
  > header { padding: 22px 28px; display: flex; align-items: center; justify-content: space-between; gap: 24px; border-bottom: 1px solid #1e293b; }
  h2 { margin: 4px 0 6px; font-size: 1.35rem; color: #f8fafc; }
  h3 { margin: 0 0 12px; font-size: .95rem; color: #f8fafc; }
  p { color: #94a3b8; font-size: .84rem; line-height: 1.6; margin: 0; }
  .eyebrow { color: #7dd3fc; font-size: .7rem; letter-spacing: .08em; text-transform: uppercase; }
  .close { border: 0; background: transparent; color: #cbd5e1; padding: 10px; cursor: pointer; }
  .scroll { overflow: auto; min-height: 0; flex: 1; }
  .columns { display: grid; grid-template-columns: minmax(0, 1fr) 340px; }
  .editor { padding: 28px; margin: 0; border: 0; min-width: 0; display: grid; gap: 24px; }
  .editor section { display: grid; gap: 16px; }
  .editor section + section { padding-top: 24px; border-top: 1px solid #1e293b; }
  aside { padding: 28px; background: #101a2a; border-left: 1px solid #1e293b; min-width: 0; }
  aside > div { position: sticky; top: 28px; display: grid; gap: 20px; }
  input, select { min-width: 0; width: 100%; box-sizing: border-box; }
  label { line-height: 1.5; }
  dl { margin: 0; } dt { color: #94a3b8; font-size: .78rem; margin-top: 16px; } dd { margin: 6px 0 0; font-size: .95rem; overflow-wrap: anywhere; }
  > footer { padding: 16px 28px; border-top: 1px solid #334155; background: #0f172a; display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-shrink: 0; }
  > header { flex-shrink: 0; }
  .error { color: #fca5a5; }
  .buttons { display: flex; gap: 8px; flex-shrink: 0; }
  button:focus-visible { outline: 2px solid #38bdf8; outline-offset: 3px; }
  @media(max-width: 850px) {
    .columns { grid-template-columns: 1fr; } aside { border-left: 0; border-top: 1px solid #1e293b; }
    > header, .editor, aside { padding: 20px; } > footer { flex-wrap: wrap; padding: 16px; }
    height: calc(100dvh - 16px);
  }
`;

interface Props {
  subtitle: string;
  assetKind: 'PC' | 'LL';
  children: React.ReactNode;
  summary: React.ReactNode;
  actions: React.ReactNode;
  busy: boolean;
  error?: string | null;
  onClose: () => void;
}

export default function LoanAmendmentWorkspace(p: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus(); };
  }, []);
  return createPortal(<Overlay>
    <Dialog ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId}
      onKeyDown={event => {
        if (event.key === 'Escape' && !p.busy) { event.stopPropagation(); p.onClose(); }
        if (event.key !== 'Tab') return;
        const items = Array.from(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') || [])
          .filter(item => item.getClientRects().length > 0);
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === ref.current)) { event.preventDefault(); first?.focus(); }
      }}>
      <header><div><span className="eyebrow">{p.assetKind === 'PC' ? 'Private credit' : 'Leveraged loan'} · Terms & amendments</span>
        <h2 id={titleId}>Prepare amendment</h2><p>{p.subtitle}</p></div>
        <button className="close" aria-label="Close amendment" disabled={p.busy} onClick={p.onClose}><X size={22} /></button></header>
      <div className="scroll"><div className="columns"><fieldset className="editor" disabled={p.busy} aria-label="Amendment inputs">{p.children}</fieldset><aside aria-label="Amendment review"><div>{p.summary}</div></aside></div></div>
      <footer><div>{p.error && <p className="error" role="alert">{p.error}</p>}<p>{p.assetKind === 'PC' ? 'Submission sends this proposal for independent approval.' : 'Applying this amendment updates the loan through its existing terms workflow.'}</p></div><div className="buttons">{p.actions}</div></footer>
    </Dialog>
  </Overlay>, document.body);
}
